import './index.scss';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Dropdown, Menu, Iconfont, Button, Modal, ButtonType } from '@umu/umu-design';
import { IMenuItemInfo, IMenuProps } from '@umu/umu-design/es/components/Menu/interface';
import debounce from 'lodash/debounce';
import { lang } from '@umu/ukit-lang';
import DepartmentSelectDialog from '../@departmentSelectDialog/departmentSelectDialog';
import { EPlatformTravelType } from '@umu/umu-types';
import { useClickAway, useHover } from 'ahooks';
import { onBatchAddAccountDialog } from './components/batchAddAccount';
import { ITransferData } from './components/batchAddAccount/transformInput';
import { If } from 'common/src/components/if';
import isArray from 'lodash/isArray';
interface ISearchItem extends IMenuItemInfo {
    id: string;
    info?: Record<string, string>;
    type?: string;
}
interface IProps extends React.HTMLAttributes<HTMLDivElement> {
    placeholder: string;
    preTitle?: string;
    multiple?: boolean;
    deleteKey?: string | string[];
    isAllowShowBatch?: boolean;
    isHideSearch?: boolean;
    isAllowShowDepartment?: boolean;
    departmentText?: string | JSX.Element;
    batchText?: string | JSX.Element;
    debounceTimeout?: number;
    menuProps?: IMenuProps;
    btnConfirmType?: ButtonType;
    onTransformMenuList?: (data: ISearchItem[], actType?: string) => ISearchItem[];
    onPreviewLoadData?: (value: string) => Promise<ITransferData[]>;
    onLoadData: (data: string) => Promise<ISearchItem[]>;
    onSelectChange?: (list: ISearchItem[], isDel?: boolean) => void;
}
const { confirm } = Modal;

const SearchInput = (props: IProps) => {
    const {
        placeholder = '',
        multiple = false,
        isAllowShowBatch = false,
        isHideSearch = false,
        batchText = lang('批量输入'),
        departmentText = lang('部门选择'),
        isAllowShowDepartment = false,
        debounceTimeout = 50,
        className = '',
        menuProps,
        preTitle = '',
        deleteKey = '',
        btnConfirmType = 'primary-blue',
        onLoadData,
        onPreviewLoadData,
        onTransformMenuList,
        onSelectChange,
        ...rest
    } = props;
    const fetchRef = useRef(0);
    const menuRef = useRef<HTMLDivElement>(null);
    const refUl = useRef<HTMLUListElement>(null);
    const [searchWord, setSearchWord] = useState('');
    const [visible, setVisible] = useState(false);
    const [isLoading, seIsLoading] = useState(false);
    const [list, setList] = useState<ISearchItem[]>([]);
    const [selectList, setSelectList] = useState<ISearchItem[]>([]);
    const isHoverMenuIng = useHover(menuRef);
    useClickAway(() => {
        if (!isHoverMenuIng) {
            onReset();
        }
    }, refUl);
    useEffect(() => {
        if (deleteKey) {
            const newSelectList = selectList.filter((item) => {
                return isArray(deleteKey) ? !deleteKey.includes(item.key) : item.key !== deleteKey;
            });
            setSelectList(newSelectList);
        }
    }, [deleteKey]);
    const onSelectListChange = (newList: ISearchItem[]) => {
        setSelectList(newList);
        onSelectChange && onSelectChange(newList, false);
    };
    const onTransformList = (data: any[], actType?: string) => {
        return onTransformMenuList ? onTransformMenuList(data, actType) : list;
    };
    const onSearch = useMemo(() => {
        const loadOptions = (value: string) => {
            if (value === '' || isLoading) {
                setList([]);
                return;
            }
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setList([]);
            seIsLoading(true);

            onLoadData(value).then((list: ISearchItem[]) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }
                setList(onTransformList(list));
                seIsLoading(false);
                setVisible(list.length > 0);
            });
        };
        return debounce(loadOptions, debounceTimeout);
    }, [fetch, debounceTimeout]);
    const onInputFocus = () => {
        if (list.length > 0) {
            setVisible(true);
            return;
        }
        !!searchWord && onSearch(searchWord);
    };
    const onSearchWordChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchWord(value);
        onSearch(value);
    };
    const onReset = () => {
        setVisible(false);
        setSearchWord('');
        setList([]);
    };
    const onDelSelect = (item: ISearchItem) => {
        const newSelectList = selectList.filter((selectItem) => selectItem.key !== item.key);
        onSelectListChange(newSelectList);
        onSelectChange && onSelectChange([item], true);
    };
    const onClick = (item: { key: string }) => {
        const { key } = item;
        const info = list.find((item) => item.key === key);
        if (!info) {
            return;
        }

        const isHas = selectList.some((item) => item.key === key);

        if (isHas) {
            onDelSelect(info);
        } else {
            onSelectListChange([...selectList, info]);
        }
        if (!multiple) {
            onReset();
        }
    };
    const onBatchSelectMember = () => {
        const dialog = onBatchAddAccountDialog({
            batchProps: {
                submitBtnType: btnConfirmType,
                onPreview: (value, callBack) => {
                    onPreviewLoadData &&
                        onPreviewLoadData(value).then(
                            (preList: ITransferData[]) => {
                                callBack(preList || []);
                            },
                            () => {
                                callBack([]);
                            },
                        );
                },
                onSubmit: (batchList: ITransferData[]) => {
                    const addList = batchList.filter(
                        (item) => !selectList.some((selectItem) => selectItem.key === item.key),
                    ) as ISearchItem[];
                    onSelectListChange([...selectList, ...addList]);
                    dialog.destroy();
                },
            },
        });
    };
    /**
     * @desc 选择部门
     */
    const onSelectDepartment = () => {
        const departmentList = selectList.filter((item) => item.key.includes('-department'));
        const ids = departmentList.map((item) => item.key.split('-')[0]).join(',');
        const dialog = confirm({
            centered: true,
            width: 'auto',
            closable: false,
            zIndex: 5000,
            content: (
                <DepartmentSelectDialog
                    isRadio={false}
                    title={lang('选择部门')}
                    departmentId={''}
                    btnConfirmType={btnConfirmType}
                    travelType={EPlatformTravelType.department}
                    defaultSelectedList={ids}
                    cbClose={() => dialog.destroy()}
                    excludeDepartmentIds={'0'}
                    cbDo={(data) => {
                        const departmentList = onTransformMenuList
                            ? onTransformMenuList(data, 'department')
                            : data.map((item) => {
                                  return {
                                      ...item,
                                      id: item.department_id,
                                      key: `${item.department_id}-department}`,
                                      title: item.department_name || '',
                                  };
                              });
                        const addList = departmentList.filter(
                            (item) => !selectList.some((selectItem) => selectItem.key === item.key),
                        );
                        onSelectListChange([...selectList, ...addList]);
                        dialog.destroy();
                    }}
                />
            ),
            footer: null,
        } as any);
    };

    const renderSelectList = () => {
        return selectList.map((item) => {
            return (
                <li className="search-member-item display-flex" key={`member-item-${item.key}`}>
                    <div>{item.title}</div>
                    <div className="search-member-item-close">
                        <Iconfont type="Close" className="icon-info-arrow-close" onClick={() => onDelSelect(item)} />
                    </div>
                </li>
            );
        });
    };
    const renderContentSuffix = () => {
        if (!isAllowShowDepartment) {
            return '';
        }
        return (
            <div className="add-department-zone display-flex" onClick={onSelectDepartment}>
                <Iconfont type="Department" className="icon-info-department" />
                <div className="department-text">{departmentText}</div>
                <Iconfont type="SmallMore" className="icon-info-arrow-right" />
            </div>
        );
    };
    const renderContent = () => {
        return (
            <div className="search-member-zone">
                <If condition={!isHideSearch}>
                    <ul className="search-member-list" ref={refUl}>
                        {renderSelectList()}
                        <li className="search-member-item search-member-input">
                            <input
                                type="text"
                                className="search-member-input-info"
                                value={searchWord}
                                onFocus={onInputFocus}
                                placeholder={selectList?.length ? '' : placeholder}
                                onChange={onSearchWordChange}
                            />
                        </li>
                    </ul>
                </If>
                {renderContentSuffix()}
            </div>
        );
    };

    return (
        <div className={`course-data-analysis-search-member ${className}`} {...rest}>
            <If condition={isAllowShowBatch}>
                <div className="display-flex">
                    {preTitle && <div>{preTitle}</div>}
                    <Button type="text" className="col-split-left" isOnlyChildren onClick={onBatchSelectMember}>
                        {batchText}
                    </Button>
                </div>
            </If>
            <If condition={!isHideSearch || isAllowShowDepartment}>
                <Dropdown
                    overlayClassName={`course-data-analysis-search-member-popup-menu-list ${className}-popup-menu-list`}
                    overlay={
                        <div ref={menuRef}>
                            <Menu
                                multiple
                                inlineIndent={8}
                                itemClassName={`popup-menu-item ${className}-popup-menu-item`}
                                list={list}
                                onClick={onClick}
                                {...menuProps}
                            />
                        </div>
                    }
                    visible={visible}
                >
                    {renderContent()}
                </Dropdown>
            </If>
        </div>
    );
};

export { SearchInput, ISearchItem };
